import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Text, Badge } from '@components/base/display';
import { Card, VStack, HStack, Grid, GridItem } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Alert, Spinner } from '@components/base/feedback';
import { Table } from '@components/base/data';
import { erpRequestLogsService } from '../../api/erpRequestLogsService';
import type { ErpRequestLog } from '../../api/erpRequestLogsService';

// ---------------------------------------------------------------------------
// Helper: format duration in milliseconds
// ---------------------------------------------------------------------------

function formatDurationMs(ms: number | null): string {
  if (ms === null) return '\u2014';
  if (ms < 1000) return `${String(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------------------
// Helper: DetailField
// ---------------------------------------------------------------------------

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <VStack gap={1}>
      <Text size="sm" color="muted" weight="medium">
        {label}
      </Text>
      <div>{children}</div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Helper: badge variant based on HTTP status code
// ---------------------------------------------------------------------------

function getStatusVariant(status: number | null): 'success' | 'error' | 'default' {
  if (status === null) return 'default';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400) return 'error';
  return 'default';
}

// ---------------------------------------------------------------------------
// Helper: render formatted JSON block
// ---------------------------------------------------------------------------

function JsonBlock({
  data,
  emptyLabel,
}: Readonly<{ data: Record<string, unknown> | null; emptyLabel: string }>) {
  if (data && Object.keys(data).length > 0) {
    return (
      <pre
        className="font-mono text-sm rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 overflow-auto"
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }

  return (
    <Text size="sm" color="muted">
      {emptyLabel}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Helper: build subtitle from log data
// ---------------------------------------------------------------------------

function buildSubtitle(log: ErpRequestLog): string | null {
  const parts: string[] = [];
  if (log.erp_endpoint?.name) parts.push(log.erp_endpoint.name);
  if (log.erp_registry?.name) parts.push(log.erp_registry.name);
  return parts.length > 0 ? parts.join(' \u2014 ') : null;
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function ErpRequestLogDetailPage() {
  const { id } = useParams<{ id: string }>();

  // ---- Fetch log data ------------------------------------------------------

  const {
    data: logResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'erp', 'request-logs', id],
    /* v8 ignore start */
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Request log ID is required'));
      return erpRequestLogsService.get(id);
    },
    /* v8 ignore stop */
    enabled: !!id,
  });

  const log = logResponse?.data;

  // ---- Render: loading state -----------------------------------------------

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading request log...</Text>
      </VStack>
    );
  }

  // ---- Render: error state -------------------------------------------------

  if (error || !log) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/erp">ERP</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/erp/request-logs">Request Logs</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Details</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load request log">
          {error instanceof Error
            ? error.message
            : 'Request log not found or an unexpected error occurred.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Derived values ------------------------------------------------------

  const shortId = log.id.slice(0, 8);
  const subtitle = buildSubtitle(log);
  const hasError = log.error_code || log.error_message;

  const queryParams: [string, string][] = (() => {
    try {
      const url = new URL(log.request_url);
      return [...url.searchParams.entries()];
    } catch {
      /* v8 ignore start */
      const qIndex = log.request_url.indexOf('?');
      if (qIndex === -1) return [];
      const qs = log.request_url.slice(qIndex + 1);
      return qs.split('&').map((pair) => {
        const [key, ...rest] = pair.split('=');
        return [decodeURIComponent(key ?? ''), decodeURIComponent(rest.join('='))];
      });
      /* v8 ignore stop */
    }
  })();

  // ---- Render: main --------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/erp">ERP</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/erp/request-logs">Request Logs</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{shortId}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header */}
      <VStack gap={2}>
        <HStack gap={3} align="center">
          <Text as="h1" size="2xl" weight="bold">
            Request Log {shortId}
          </Text>
          <Badge
            variant={getStatusVariant(log.response_status)}
            size="sm"
          >
            {log.response_status ?? 'N/A'}
          </Badge>
        </HStack>
        {subtitle && (
          <Text size="sm" color="muted">
            {subtitle}
          </Text>
        )}
      </VStack>

      {/* Request Details card */}
      <Card>
        <Card.Header title="Request Details" />
        <Card.Content>
          <Grid columns={2} gap={4}>
            <GridItem>
              <DetailField label="Trace ID">
                <Text size="sm" className="font-mono">
                  {log.trace_id}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Method">
                <Badge variant="info" size="sm">
                  {log.request_method}
                </Badge>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="URL">
                <Text size="sm" className="font-mono break-all">
                  {log.request_url.split('?')[0]}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Response Status">
                <Badge
                  variant={getStatusVariant(log.response_status)}
                  size="sm"
                >
                  {log.response_status ?? '\u2014'}
                </Badge>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Duration">
                <Text size="sm">{formatDurationMs(log.duration_ms)}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Retry Count">
                <Text size="sm">{log.retry_count}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Registry">
                <Text size="sm">{log.erp_registry?.name ?? '\u2014'}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Endpoint">
                <Text size="sm">{log.erp_endpoint?.name ?? '\u2014'}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Created At">
                <Text size="sm">
                  {new Date(log.created_at).toLocaleString()}
                </Text>
              </DetailField>
            </GridItem>
          </Grid>
        </Card.Content>
      </Card>

      {/* Query Parameters card (conditional) */}
      {queryParams.length > 0 && (
        <Card>
          <Card.Header title="Query Parameters" />
          <Card.Content>
            <Table>
              <Table.Head>
                <Table.Row hoverable={false}>
                  <Table.HeaderCell>Parameter</Table.HeaderCell>
                  <Table.HeaderCell>Value</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {queryParams.map(([key, value]) => (
                  <Table.Row key={key} hoverable={false}>
                    <Table.Cell>
                      <Text size="sm" className="font-mono" weight="medium">{key}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="sm" className="font-mono">{value}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Card.Content>
        </Card>
      )}

      {/* Error Information card (conditional) */}
      {hasError && (
        <Card>
          <Card.Header title="Error Information" />
          <Card.Content>
            <VStack gap={3}>
              {log.error_code && (
                <DetailField label="Error Code">
                  <Badge variant="error" size="sm">
                    {log.error_code}
                  </Badge>
                </DetailField>
              )}
              {log.error_message && (
                <DetailField label="Error Message">
                  <Text>{log.error_message}</Text>
                </DetailField>
              )}
            </VStack>
          </Card.Content>
        </Card>
      )}

      {/* Request Headers card */}
      <Card>
        <Card.Header title="Request Headers" />
        <Card.Content>
          <JsonBlock data={log.request_headers} emptyLabel="No request headers" />
        </Card.Content>
      </Card>

      {/* Request Body card */}
      <Card>
        <Card.Header title="Request Body" />
        <Card.Content>
          <JsonBlock data={log.request_body} emptyLabel="No request body" />
        </Card.Content>
      </Card>

      {/* Response Headers card */}
      <Card>
        <Card.Header title="Response Headers" />
        <Card.Content>
          <JsonBlock data={log.response_headers} emptyLabel="No response headers" />
        </Card.Content>
      </Card>

      {/* Response Body card */}
      <Card>
        <Card.Header title="Response Body" />
        <Card.Content>
          <JsonBlock data={log.response_body} emptyLabel="No response body" />
        </Card.Content>
      </Card>

      {/* Transformed Response card (conditional) */}
      {log.transformed_response && Object.keys(log.transformed_response).length > 0 && (
        <Card>
          <Card.Header title="Transformed Response" />
          <Card.Content>
            <JsonBlock data={log.transformed_response} emptyLabel="No transformed response" />
          </Card.Content>
        </Card>
      )}
    </VStack>
  );
}
